/**
 * PortalAuditPanel — the P2 §4.7 audit viewer for the clinician portal: the
 * staff-side action trail and the clinicians' own activity log, queried on
 * demand with offset paging.
 *
 * A QUERY screen, not a feed. Nothing loads until Load is pressed — an audit
 * read is a deliberate act, and a tab that auto-fetched on every visit would
 * pull PHI-flagged rows for someone who only came to check a practice's fax
 * number. The PHI-only filter defaults ON for the clinician source because the
 * portal's version-poll rows are noise there; staff rows are all deliberate
 * actions, so that source defaults to everything.
 */
import { useState } from 'react';
import { Loader2, ScrollText } from 'lucide-react';
import api from '../../../services/api';
import { FilterBar, SegmentedControl } from '../../components/filters';
import { fieldCls, primaryBtnCls, secondaryBtnCls } from '../../components/filters/styles';
import { Alert } from '../../components/ui';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

const LIMIT = 50;

type AuditSource = 'staff' | 'clinician';

/**
 * One row from GET /api/crm/portal-admin/audit. The two sources share the core
 * columns; the rest are source-specific and simply absent on the other one.
 */
interface AuditRow {
  id: string;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  phi_accessed: boolean;
  details: unknown;
  occurred_at: string;
  // staff source
  user_id?: string | null;
  user_email?: string | null;
  // clinician source
  principal_type?: string | null;
  portal_user_id?: string | null;
  contact_id?: string | null;
  session_id?: string | null;
  org_id?: string | null;
  status_code?: number | null;
}

const thCls = 'px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400';
const tdCls = 'px-3 py-2 align-top text-xs text-gray-700 dark:text-gray-300';
const idCls = 'font-mono text-[11px] text-gray-500 dark:text-gray-400 break-all';

const fmtTs = (iso: string) => new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

/** Monospace id cell content, or an em-dash when the column is null. */
function Id({ value }: { value: string | null | undefined }) {
  return value ? <span className={idCls}>{value}</span> : <span className="text-gray-400">—</span>;
}

export default function PortalAuditPanel() {
  // ids stay ids here BY DESIGN (P2 §4.7): resolving them to names would make the audit viewer a second PHI surface.
  const [source, setSource] = useState<AuditSource>('staff');
  const [action, setAction] = useState('');
  const [phiOnly, setPhiOnly] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const changeSource = (next: AuditSource) => {
    setSource(next);
    // Default ON for the clinician source — the portal's version-poll rows are
    // noise, and the question this tab answers is "who saw PHI".
    setPhiOnly(next === 'clinician');
    setRows([]);
    setLoaded(false);
    setHasMore(false);
    setError(null);
    setExpanded(new Set());
  };

  const load = async (reset: boolean) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/crm/portal-admin/audit', {
        params: {
          source,
          action: action.trim() || undefined,
          phi: phiOnly ? '1' : undefined,
          from: fromDate || undefined,
          to: toDate || undefined,
          limit: LIMIT,
          offset: reset ? 0 : rows.length,
        },
      });
      const next: AuditRow[] = res.data?.rows || [];
      setRows(reset ? next : [...rows, ...next]);
      setHasMore(next.length === LIMIT);
      setLoaded(true);
      if (reset) setExpanded(new Set());
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const detailsCell = (r: AuditRow) => {
    if (r.details === null || r.details === undefined) return <span className="text-gray-400">—</span>;
    const open = expanded.has(r.id);
    return open ? (
      <button type="button" onClick={() => toggleRow(r.id)} aria-expanded="true" title="Collapse" className="block max-w-md text-left">
        <code className="block whitespace-pre-wrap break-all rounded bg-gray-50 p-2 text-[11px] text-gray-700 dark:bg-gray-900/40 dark:text-gray-300">
          {JSON.stringify(r.details, null, 2)}
        </code>
      </button>
    ) : (
      <button type="button" onClick={() => toggleRow(r.id)} aria-expanded="false" title="Expand" className="block max-w-[16rem] truncate text-left">
        <code className="text-[11px] text-gray-500 dark:text-gray-400">{JSON.stringify(r.details)}</code>
      </button>
    );
  };

  const phiCell = (r: AuditRow) =>
    r.phi_accessed ? (
      <span className="inline-flex rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700 dark:bg-red-900/40 dark:text-red-300">
        PHI
      </span>
    ) : (
      <span className="text-gray-400">—</span>
    );

  return (
    <div className="space-y-3">
      <FilterBar>
        <SegmentedControl
          label="Audit source"
          value={source}
          onChange={(v) => changeSource(v as AuditSource)}
          options={[
            { value: 'staff', label: 'Staff actions' },
            { value: 'clinician', label: 'Clinician activity' },
          ]}
        />
        <input
          className={`${fieldCls} min-h-[44px] w-52 sm:min-h-0`}
          value={action}
          onChange={(e) => setAction(e.target.value)}
          placeholder="Action prefix (e.g. referral.)"
          aria-label="Action prefix"
        />
        <label className="inline-flex min-h-[44px] items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400 sm:min-h-0">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300"
            checked={phiOnly}
            onChange={(e) => setPhiOnly(e.target.checked)}
          />
          PHI only
        </label>
        <input
          type="date"
          className={`${fieldCls} min-h-[44px] sm:min-h-0`}
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          aria-label="From date"
        />
        <span className="text-xs text-gray-400">to</span>
        <input
          type="date"
          className={`${fieldCls} min-h-[44px] sm:min-h-0`}
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          aria-label="To date"
        />
        <button type="button" className={primaryBtnCls} onClick={() => load(true)} disabled={loading}>
          {loading && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
          Load
        </button>
        {loaded && (
          <span className="text-xs text-gray-400" aria-live="polite">
            {rows.length} row{rows.length === 1 ? '' : 's'} loaded
          </span>
        )}
      </FilterBar>

      {error && (
        <Alert tone="error" icon={false}>{error}</Alert>
      )}

      {!loaded && !loading ? (
        <div className="rounded-xl border border-dashed border-gray-300 p-10 text-center dark:border-gray-600">
          <ScrollText className="mx-auto mb-3 h-10 w-10 text-gray-300 dark:text-gray-600" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Audit rows load on demand — pick a source, set the filters, and press Load.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                {source === 'staff' ? (
                  <tr>
                    <th className={thCls}>When</th>
                    <th className={thCls}>Staff user</th>
                    <th className={thCls}>Action</th>
                    <th className={thCls}>Resource</th>
                    <th className={thCls}>PHI</th>
                    <th className={thCls}>Details</th>
                  </tr>
                ) : (
                  <tr>
                    <th className={thCls}>When</th>
                    <th className={thCls}>Actor</th>
                    <th className={thCls}>Action</th>
                    <th className={thCls}>Resource</th>
                    <th className={thCls}>Contact</th>
                    <th className={thCls}>Practice</th>
                    <th className={thCls}>Session</th>
                    <th className={thCls}>Status</th>
                    <th className={thCls}>PHI</th>
                    <th className={thCls}>Details</th>
                  </tr>
                )}
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={source === 'staff' ? 6 : 10} className="px-3 py-8 text-center">
                      <Loader2 className="mx-auto h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" />
                    </td>
                  </tr>
                )}
                {!loading && loaded && rows.length === 0 && (
                  <tr>
                    <td colSpan={source === 'staff' ? 6 : 10} className="px-3 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                      No audit rows match those filters.
                    </td>
                  </tr>
                )}
                {rows.map((r) =>
                  source === 'staff' ? (
                    <tr key={r.id} className="transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className={`${tdCls} whitespace-nowrap`}>{fmtTs(r.occurred_at)}</td>
                      <td className={tdCls}>
                        <div>{r.user_email || <span className="text-gray-400">—</span>}</div>
                        <Id value={r.user_id} />
                      </td>
                      <td className={`${tdCls} font-medium text-gray-900 dark:text-gray-100`}>{r.action}</td>
                      <td className={tdCls}>
                        <div>{r.resource_type || <span className="text-gray-400">—</span>}</div>
                        <Id value={r.resource_id} />
                      </td>
                      <td className={tdCls}>{phiCell(r)}</td>
                      <td className={tdCls}>{detailsCell(r)}</td>
                    </tr>
                  ) : (
                    <tr key={r.id} className="transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className={`${tdCls} whitespace-nowrap`}>{fmtTs(r.occurred_at)}</td>
                      <td className={tdCls}>
                        <div>{r.principal_type || <span className="text-gray-400">—</span>}</div>
                        <Id value={r.portal_user_id} />
                      </td>
                      <td className={`${tdCls} font-medium text-gray-900 dark:text-gray-100`}>{r.action}</td>
                      <td className={tdCls}>
                        <div>{r.resource_type || <span className="text-gray-400">—</span>}</div>
                        <Id value={r.resource_id} />
                      </td>
                      <td className={tdCls}>
                        <Id value={r.contact_id} />
                      </td>
                      <td className={tdCls}>
                        <Id value={r.org_id} />
                      </td>
                      <td className={tdCls}>
                        <Id value={r.session_id} />
                      </td>
                      <td className={tdCls}>
                        {typeof r.status_code === 'number' ? (
                          <span className={`tabular-nums ${r.status_code >= 400 ? 'font-medium text-red-600 dark:text-red-400' : ''}`}>
                            {r.status_code}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className={tdCls}>{phiCell(r)}</td>
                      <td className={tdCls}>{detailsCell(r)}</td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {loaded && hasMore && (
        <div className="flex justify-center">
          <button type="button" className={secondaryBtnCls} onClick={() => load(false)} disabled={loading}>
            {loading && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            Load more
          </button>
        </div>
      )}
    </div>
  );
}
